/**
 * HmPaymentDetail — formatka dokumentu kasowego (PODGLĄD).
 *
 * Zamiast pozycji towarowych ma ROZLICZENIA: do jakich dokumentów przypisano wpłatę lub wypłatę.
 * Dlatego jedyną zakładką jest „Rozliczenia" — pozycji w rozumieniu towarów tu nie ma.
 *
 * Kod typu (KP/KW/BP/BW) Symfonia podaje dopiero TUTAJ — na liście go nie ma. Formatka pokazuje
 * go więc jako pełnoprawne pole, mimo że kolumna listy w tym samym miejscu miała kreskę.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { HmPaymentDetail as HmPaymentDetailDto } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import {
  day,
  DetailError,
  DetailHeader,
  DetailTabBody,
  DetailTabs,
  Field,
  PdfPreviewModal,
  Section,
  TotalsBar,
  useDocumentTabLabel,
  usePdfPreview,
} from '@/components/handel-magazyn/detail-parts';
import {
  PrintDocumentModal,
  printSettingsToParams,
} from '@/components/handel-magazyn/PrintDocumentModal';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TABS = ['settlements'] as const;

export function HmPaymentDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const companyId = useActiveCompanyScope().companyId;
  const pdf = usePdfPreview(`/handel-magazyn/payments/${id}/pdf?companyId=${companyId}`);
  const [printOpen, setPrintOpen] = useState(false);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'payment', id, companyId],
    enabled: !!id && !!companyId,
    retry: false,
    queryFn: async (): Promise<HmPaymentDetailDto> => {
      const res = await api.get<HmPaymentDetailDto>(
        `/handel-magazyn/payments/${id}?companyId=${companyId}`,
      );
      return res.data;
    },
  });

  // Numer dokumentu znamy dopiero po odpowiedzi — podmieniamy etykiete karty
  // z ogolnej na konkretny numer.
  useDocumentTabLabel(`/handel-magazyn/kasa-bank/${id}`, query.data?.documentNumber);

  if (query.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }
  if (query.error) return <DetailError error={query.error} />;

  const doc = query.data!;

  return (
    <div className="flex h-full flex-col p-4">
      <DetailHeader
        documentNumber={doc.documentNumber}
        onPrint={() => setPrintOpen(true)}
        printLoading={pdf.loading}
      />

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.documentType')}>
            <Field label={t('tenant.handelMagazyn.columns.typeCode')} value={doc.typeCode} />
            <Field
              label={t('tenant.handelMagazyn.payments.columns.operation')}
              value={doc.paymentTypeLabel}
            />
            <Field
              label={t('tenant.handelMagazyn.payments.columns.registry')}
              value={doc.registryName}
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.payments.columns.subject')}>
            <Field label={t('tenant.handelMagazyn.detail.name')} value={doc.subjectName} />
            <Field
              label={t('tenant.handelMagazyn.payments.subjectType')}
              value={doc.subjectTypeLabel}
            />
          </Section>
        </div>

        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.payment')}>
            <Field label={t('tenant.handelMagazyn.detail.currency')} value={doc.currency} />
            <Field
              label={t('tenant.handelMagazyn.payments.canceled')}
              value={
                doc.canceled
                  ? t('tenant.handelMagazyn.detail.yes')
                  : t('tenant.handelMagazyn.detail.no')
              }
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.detail.dates')}>
            <Field label={t('tenant.handelMagazyn.columns.issueDate')} value={day(doc.issueDate)} />
            <Field
              label={t('tenant.handelMagazyn.columns.maturityDate')}
              value={day(doc.maturityDate)}
            />
            <Field
              label={t('tenant.handelMagazyn.payments.columns.settlementDate')}
              value={day(doc.settlementDate)}
            />
          </Section>
        </div>
      </div>

      <DetailTabs tabs={TABS} active="settlements" onChange={() => {}} />

      <DetailTabBody>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
              <th className="px-2 py-1.5 font-medium">
                {t('tenant.handelMagazyn.payments.settlement.document')}
              </th>
              <th className="px-2 py-1.5 text-right font-medium">
                {t('tenant.handelMagazyn.payments.settlement.value')}
              </th>
            </tr>
          </thead>
          <tbody>
            {doc.settlements.length === 0 && (
              <tr>
                <td colSpan={2} className="px-2 py-6 text-center text-text-tertiary">
                  {t('tenant.handelMagazyn.payments.settlement.none')}
                </td>
              </tr>
            )}
            {doc.settlements.map((s) => (
              <tr key={s.id} className="border-b border-border-primary/60">
                <td className="px-2 py-1.5 font-mono text-xs text-text-primary">
                  {s.settledDocumentNumber ?? '—'}
                </td>
                <td className="px-2 py-1.5 text-right font-medium text-text-primary">{s.value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {doc.description && (
          <div className="mt-3">
            <Field label={t('tenant.handelMagazyn.detail.description')} value={doc.description} />
          </div>
        )}
      </DetailTabBody>

      <TotalsBar
        items={[
          { label: t('tenant.handelMagazyn.payments.columns.settled'), value: doc.settledValue },
          {
            label: t('tenant.handelMagazyn.payments.columns.total'),
            value: `${doc.totalValue} ${doc.currency}`,
            strong: true,
          },
        ]}
      />

      <PdfPreviewModal url={pdf.url} title={doc.documentNumber} onClose={pdf.close} />
      <PrintDocumentModal
        open={printOpen}
        kind="payments"
        companyId={companyId}
        documentNumber={doc.documentNumber}
        onClose={() => setPrintOpen(false)}
        onPrint={(settings) => {
          setPrintOpen(false);
          void pdf.show(printSettingsToParams(settings));
        }}
      />
    </div>
  );
}

export default HmPaymentDetail;
